"use client";
import Link from "next/link";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { conferencesEvents as data } from "@/lib/data/press";

const accent = "var(--color-amber)";

export function ConferencesEvents() {
  return (
    <Section id="conferences-events" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-amber)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Event</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Location</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Date</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden lg:table-cell">Topic</th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcoming.map((e) => (
                    <tr key={e.event + e.date} className="border-b border-[rgb(var(--border)/0.08)]">
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-baseline gap-2">
                          <Calendar className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} />
                          <span className="font-medium">{e.event}</span>
                        </div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 md:hidden flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 lg:hidden">{e.topic}</div>
                      </td>
                      <td className="px-4 py-3 align-top hidden md:table-cell text-[rgb(var(--fg-muted))]"><div className="flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</div></td>
                      <td className="px-4 py-3 align-top font-mono text-xs" style={{ color: accent }}>{e.date}</td>
                      <td className="px-4 py-3 align-top hidden lg:table-cell text-[rgb(var(--fg-muted))]">{e.topic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <div className="glass rounded-2xl p-6" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Past Event Highlights</span>
              <div className="mt-3 space-y-2">
                {data.past.slice(0, 4).map((p, i) => (
                  <div key={i} className="text-sm">
                    <div className="font-semibold">{p.event} <span className="text-xs text-[rgb(var(--fg-muted))]">({p.year})</span></div>
                    <div className="text-xs text-[rgb(var(--fg-muted))]">{p.topic} · {p.attendance}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Speaking Topics</span>
              <ul className="mt-3 space-y-1.5">
                {data.topics.map((t, i) => (
                  <li key={i} className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
